import React, { ComponentType } from 'react';
import { Typography } from '@cp/ds/src/components/typography';
import { styled } from '@cp/ds/src/theme';
import { useConfig } from '@cp/shared/config';
import { Article, ArticleCssTokens, BoxProps } from '@youhelp-ru/ui';
import { RoutedLink as Link, RoutedLink } from 'src/shared/ui/link';
import type { LegalConfig } from '../model';

const Root = styled(Article, { name: 'UserAgreement' })(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.fontSize,
  [ArticleCssTokens.gap]: theme.spacing(2),
  'p': {
    ...theme.typography.body,
  },
})) as unknown as ComponentType<BoxProps>;

export const UserAgreementWidget = () => {
  const config = useConfig<LegalConfig>();
  const webOrigin = String(config.get('CLIENT_WEB_ORIGIN'));
  const privacyPolicyUrl = `${webOrigin}/privacy-policy/`;

  return (
    <Root>
      <Typography component="h1" variant="h1">
        Правила пользования сайтом и приложением
      </Typography>
      <Link href={`${config.get('CLIENT_WEB_ORIGIN', '')}/`}>{config.get('CLIENT_WEB_ORIGIN')}</Link>
      <section>
        <p>
          Данные правила регулируют особенности применения информационных ресурсов на портале и в приложении. Данные сервисы предоставляет{' '}
          {config.get('LEGAL_NAME')}, зарегистрированное по адресу: Россия, {config.get('LEGAL_ADDRESS')}, ИНН {config.get('INN')}, ОГРН{' '}
          {config.get('OGRN')}. Применяя данные сервисы, Вы выражаете согласие с вышеуказанными Правилами и Политикой обработки персональных
          данных, размещённой по адресу <RoutedLink href={privacyPolicyUrl}>{privacyPolicyUrl}</RoutedLink>.
        </p>
      </section>
      <Typography component="h4" sx={{ typography: { xs: 'h6', md: 'h4' }, m: 0, mb: 2.75 }}>
        Терминология
      </Typography>

      <p>
        <b>Сайт</b> — программное обеспечение и информация, размещенные в Сети по адресу{' '}
        <RoutedLink href={config.get('CLIENT_WEB_ORIGIN', '')}>{String(config.get('CLIENT_WEB_ORIGIN'))}</RoutedLink>, для совершения
        пользователями сделок.
      </p>

      <p>
        <b>Приложение</b> — ПО, работающее на смартфонах, планшете, других электронных устройствах на ОС iOS и Android.
      </p>

      <p>
        <b>{config.get('LEGAL_NAME')}</b> — юридическое лицо с ограниченной ответственностью, которое является администратором Сайта и
        оказывает пользователям определенный набор услуг.
      </p>

      <p>
        <b>Пользователь</b> — физическое лицо с дееспособностью или его законный представитель, использующий Сайт в допустимых целях. Он
        может пройти регистрацию на портале или просматривать его страницы без регистрации. К пользователям относятся и индивидуальные
        предприниматели.
      </p>

      <p>
        <b>Специалист</b> — пользователь интернет-ресурса или приложения, физлицо, зарегистрированное в базе компании для дальнейшего
        взаимодействия с Нанимателем.
      </p>

      <p>
        <b>Наниматель</b> — гражданин, организация или ИП, которые заключают гражданско-правовой договор со Специалистами.
      </p>

      <p>
        <b>Анкета</b> — многостраничный файл, опубликованный в приложении или интернет-ресурсе, включающий персональную информацию о
        Специалисте, в том числе его ФИО, электронную почту, образование, места трудовой деятельности.
      </p>

      <p>
        <b>Объявление</b> — информация об основных требованиях к актуальной должности у Нанимателя, условиях трудовой деятельности,
        информация о Нанимателе, которую данное лицо захотело указать.
      </p>

      <p>
        Сервис YouHelp предоставляет доступ к функционалу платформы на безвозмездной основе и не взимает плату за использование основных
        возможностей.
      </p>

      <section>
        <Typography component="h4" sx={{ typography: { xs: 'h6', md: 'h4' }, m: 0, mb: 2.75 }}>
          <b>1.</b> Особенности пользовательского соглашения
        </Typography>

        <p>
          <b>1.1</b> Организация {config.get('LEGAL_NAME')} намеревается заключить такое соглашение с лицами, имеющими статус Пользователя,
          для оказания им услуг информационного характера и предоставления сервисов.
        </p>

        <p>
          <b>1.2</b> Пользователь принимает Правила, заключает Соглашение при регистрации на интернет-ресурсе или в приложении или же без
          регистрации, при просмотре страниц. Заключение Соглашения идентично оформлению документа в письменной форме.
        </p>

        <p>
          <b>1.3</b> При начале использования сервисов лицо подтверждает свое ознакомление с Правилами.
        </p>

        <p>
          <b>1.4</b> Условия Соглашения принимаются Пользователем посредством присоединения к Правилам в соответствии со ст. 428 ГК РФ.
        </p>

        <p>
          <b>1.5</b> Стороны заключают между собой договор для оказания услуг с помощью сайта или приложения. При этом Сервис обеспечивает
          возможность взаимодействия между Пользователями. {config.get('LEGAL_NAME')} не является стороной договоренностей между
          Пользователями и не участвует в их заключении.
        </p>
      </section>
      <section>
        <Typography component="h4" sx={{ typography: { xs: 'h6', md: 'h4' }, m: 0, mb: 2.75 }}>
          <b>2.</b> Функциональные особенности сервисов
        </Typography>

        <p>
          <b>2.1</b> Сервисы предоставляют информационную помощь, содействие в отборе кандидатур для проведения личных собеседований.
        </p>
        <Typography component="ul" sx={{ my: 2 }}>
          <Typography component="li">Есть возможность размещения Анкет.</Typography>
          <Typography component="li">
            Специалист может откликаться на приглашения записаться на интервью, в том числе через техническую поддержку сервиса.
          </Typography>
          <Typography component="li">Наниматель может публиковать вакансии.</Typography>
        </Typography>

        <p>
          <b>2.2</b> У {config.get('LEGAL_NAME')} есть следующие права:
        </p>
        <Typography component="ul" sx={{ my: 2 }}>
          <Typography component="li">Привлечение третьих лиц для выполнения своих обязанностей.</Typography>
          <Typography component="li">
            Информирование сторон о различных событиях по контактам, которые они предоставили, до момента их отказа от проведения рассылки.
          </Typography>
          <Typography component="li">Изменение требований к анкетам.</Typography>
          <Typography component="li">Редактирование анкет, чтобы сделать их более удобными для Нанимателей.</Typography>
          <Typography component="li">Отказ от вступления в переписку с создателями анкет.</Typography>
          <Typography component="li">
            Удаление анкеты в любое время, в том числе без мотивированного отказа, а также отказ в публикации.
          </Typography>
        </Typography>

        <p>
          <b>2.3.</b> {config.get('LEGAL_NAME')} не является работодателем, агентством занятости, кадровым агентом или представителем одной
          из сторон. Компания оказывает исключительно информационно-технические услуги, предоставляя пользователям платформу для
          взаимодействия.
        </p>
      </section>
      <section>
        <Typography component="h4" sx={{ typography: { xs: 'h6', md: 'h4' }, m: 0, mb: 2.75 }}>
          <b>3.</b> Правомочия и обязательства Пользователя
        </Typography>
        <Typography component="h6" sx={{ typography: { xs: 'button', md: 'h6' }, m: 0, mb: 2.5 }}>
          У этих сторон есть следующие обязанности:
        </Typography>
        <p>
          <b>3.1</b> Нельзя размещать, сохранять или загружать различными способами информацию, контент, противоречащие российскому
          законодательству и международному праву. К такой информации относится:
        </p>
        <Typography component="ul" sx={{ my: 2 }}>
          <Typography component="li">
            Сведения, оскорбляющие честь и достоинства, нравственность других людей, нарушающие авторство, разжигающие ненависть по расовым,
            религиозным, половым и другим признакам, сцены насилия, жестокости по отношению к животным.
          </Typography>
          <Typography component="li">
            Информация, ведущая к уничтожению или ухудшению функциональности ПО, для получения неправомерного доступа к программным
            продуктам или платным источникам в Сети.
          </Typography>
          <Typography component="li">Пропаганда наркотиков, психотропных средств и спиртного.</Typography>
          <Typography component="li">
            Сведения, нарушающие общепринятые моральные нормы, незаконно использующие информацию о третьих лицах (например, публикация
            телефонного номера без согласия владельца).
          </Typography>
          <Typography component="li">Порнография, материалы эротического характера.</Typography>
        </Typography>
        <p>
          <b>3.2</b> Нельзя размещать или передавать материалы, являющиеся интеллектуальной собственностью.
        </p>
        <p>
          <b>3.3</b> Не допускается нарушать информационную безопасность сервисов.
        </p>
        <p>
          <b>3.4</b> Необходимо использовать только свои персональные данные, уведомлять о том, что Вы представляете интересы другого лица,
          не вводить в заблуждение других лиц по поводу своей идентификации.
        </p>
        <p>
          <b>3.5</b> Не следует удалять тексты и материалы, автором которых Вы не являетесь.
        </p>
        <p>
          <b>3.6</b> Необходимо информировать {config.get('LEGAL_NAME')} о действиях других лиц, если они нарушают Правила.
        </p>
        <p>
          <b>3.7</b> Используйте ресурсы сервисов только в целях, обозначенных Соглашением.
        </p>
        <p>
          <b>3.8</b> Не размещайте вакансии от любых работодателей и анкеты от любых Специалистов, в том числе от тех, у которых нет
          намерения подобрать вакансию.
        </p>
        <p>
          <b>3.9</b> Не публикуйте рекламные вакансии без предварительного согласования с Оператором, особенно схемы-«пирамиды», носящие
          мошеннический характер.
        </p>
        <p>
          <b>3.10</b> Пользователи несут личную ответственность перед другими лицами за свои действия, выполненные с помощью Приложения или
          Сайта.
        </p>
        <p>
          <b>3.11</b> У Пользователей есть следующие права:
        </p>
        <Typography component="ul" sx={{ my: 2 }}>
          <Typography component="li">
            Получение от {config.get('LEGAL_NAME')} консультаций по техническим аспектам при публикации, редактировании или удалении
            информации.
          </Typography>
          <Typography component="li">Отказ или ограничение на получение сообщений от других лиц, использующих сервисы.</Typography>
          <Typography component="li">Передача, изменение, удаление сведений с помощью функциональных возможностей сервисов.</Typography>
          <Typography component="li">Удаление собственного профиля.</Typography>
        </Typography>
      </section>
      <section>
        <Typography component="h4" sx={{ typography: { xs: 'h6', md: 'h4' }, m: 0, mb: 2.75 }}>
          <b>4.</b> Особенности регистрации специалистов
        </Typography>

        <p>
          <b>4.1</b> Для регистрации на Сайте или в Приложении со статусом Специалиста выберите нужный раздел в меню, заполните анкету и
          подтвердите свое согласие с условиями. Регистрация осуществляется по номеру телефона, к одному номеру можно привязать только один
          профиль.
        </p>
        <p>
          <b>4.2</b> Укажите следующую информацию, которая будет размещена в открытом доступе:
        </p>
        <Typography component="ul" sx={{ my: 2 }}>
          <Typography component="li">Ваше имя и первая буква фамилии.</Typography>
          <Typography component="li">Страна и город проживания.</Typography>
          <Typography component="li">Вакансия.</Typography>
          <Typography component="li">График трудовой деятельности.</Typography>
          <Typography component="li">Описание в графе «Обо мне».</Typography>
          <Typography component="li">Стоимость услуг.</Typography>
          <Typography component="li">Ссылки на страницы в соцсетях.</Typography>
          <Typography component="li">Возраст.</Typography>
        </Typography>
        <p>
          Также укажите данные, которые не будут размещаться открыто без Вашего согласия, кроме случаев, предусмотренных по закону или
          соглашению:
        </p>
        <Typography component="ul" sx={{ my: 2 }}>
          <Typography component="li">ФИО.</Typography>
          <Typography component="li">Дата Вашего рождения.</Typography>
          <Typography component="li">Адрес, по которому Вы зарегистрированы.</Typography>
          <Typography component="li">Почтовый адрес.</Typography>
          <Typography component="li">Данные из паспорта.</Typography>
          <Typography component="li">ИНН.</Typography>
        </Typography>

        <p>
          <b>4.3.</b> Регистрируясь на Сайте или в Приложении, Специалист подтверждает согласие на обработку своих персональных данных в
          объёме, необходимом для использования функционала сервиса и подтверждает ознакомление с Политикой конфиденциальности.
        </p>
      </section>
      <section>
        <Typography component="h4" sx={{ typography: { xs: 'h6', md: 'h4' }, m: 0, mb: 2.75 }}>
          <b>5.</b> Особенности ответственности Пользователей
        </Typography>

        <p>
          <b>5.1</b> Правовые отношения возникают между Нанимателем и Специалистом, {config.get('LEGAL_NAME')} не выступает стороной таких
          отношений.
        </p>

        <p>
          <b>5.2</b> Специалист берет на себя обязанности перед Нанимателем, отвечает за полные и достоверные сведения о себе, качество
          услуг. Наниматель берет на себя возможные риски, связанные с сотрудничеством со второй стороной.
        </p>

        <p>
          <b>5.3</b> {config.get('LEGAL_NAME')} не несет ответственности за эти договоренности и материальные обязательства.
        </p>

        <p>
          <b>5.4</b> {config.get('LEGAL_NAME')} не дает гарантии сторонам, что вакансии и анкеты, контакты, указанные в них, соответствуют
          действительности.
        </p>

        <p>
          <b>5.5</b> Организация не гарантирует определенное число просмотров или откликов.
        </p>

        <p>
          <b>5.6</b> Сервисы предоставляются по принципу «как есть». Организация не гарантирует, что ПО не имеет ошибок. При утрате
          информации или поломке оборудования {config.get('LEGAL_NAME')} не берет на себя ответственность за это.
        </p>

        <p>
          <b>5.7</b> {config.get('LEGAL_NAME')} не отвечает за ущерб или убытки, связанные с применением сервисов или невозможностью их
          применения.
        </p>

        <Typography component="p" sx={{ typography: { xs: 'body bold', md: 'button' } }}>
          <b>5.8</b> {config.get('LEGAL_NAME')} не отвечает за следующее:
        </Typography>
        <Typography component="ul" sx={{ my: 2 }}>
          <Typography component="li">Качество услуг специалистов.</Typography>
          <Typography component="li">Достоверность сведений от Пользователей.</Typography>
        </Typography>

        <p>
          <b>5.9</b> Пользователи сами отвечают за невыполненные обязанности перед другими лицами, в том числе перед{' '}
          {config.get('LEGAL_NAME')}.
        </p>

        <p>
          <b>5.10</b> Пользователи берут ответственность за нарушения, в том числе в сфере авторских прав и прав на методы индивидуализации,
          например, на товарные знаки.
        </p>

        <p>
          <b>5.11</b> У {config.get('LEGAL_NAME')} есть право без предупреждения удалить сведения или заблокировать к ним доступ, если есть
          подозрения, что они нарушают права других лиц, в том числе по письменному заявлению правообладателя. Информация, удаленная по
          запросу правообладателя, восстанавливается по его заявлению или на основании судебного решения.
        </p>

        <p>
          <b>5.12</b> Организация может предварительно модерировать размещаемую информацию или отказать в публикации сведений.{' '}
          {config.get('LEGAL_NAME')} не редактирует содержание информации без согласования со Специалистами, кроме правки грамматических
          ошибок, не отвечает за достоверность сведений.
        </p>

        <p>
          <b>5.13</b> Если {config.get('LEGAL_NAME')} привлекается к ответственности по закону по причине действий или бездействия
          Пользователей, последние обязуются возместить все убытки, в том числе затраты на юридические услуги.
        </p>

        <p>
          <b>5.14</b> Пользователь отвечает за действия, выполняемые с помощью его личного кабинета, а значит, он должен защищать свои
          учетные данные.
        </p>

        <p>
          <b>5.15</b> {config.get('LEGAL_NAME')} может заблокировать личный кабинет лица, если посчитает, что оно нарушило Правила или
          закон, на него поступали неоднократные жалобы от других Пользователей. О блокировке Пользователь может быть предупрежден по
          каналам электронной связи.
        </p>

        <p>
          <b>5.16</b> Материалы, опубликованные на Сайте или в Приложении, относятся к объектам интеллектуальной собственности{' '}
          {config.get('LEGAL_NAME')} или Пользователей – авторов таких материалов, при условии размещения ссылки на Сайт. Организация или
          Пользователи могут отказать в применении этих материалов или определить приемлемые условия для их использования.
        </p>
      </section>
      <section>
        <Typography component="h4" sx={{ typography: { xs: 'h6', md: 'h4' }, m: 0, mb: 2.75 }}>
          <b>6.</b> Конфиденциальность, работа с персональными данными
        </Typography>

        <p>
          <b>6.1</b> В ходе регистрации в Приложении или на Сайте Вы подтверждаете, что соглашаетесь с особенностями обработки персональных
          данных, выражаете согласие на обработку такой информации.
        </p>

        <p>
          <b>6.2</b> Вы соглашаетесь, что персональную информацию, которую вы передаете {config.get('LEGAL_NAME')}, в том числе при
          оформлении сделок, можно передавать третьим лицам, например, остальным Пользователям.
        </p>

        <p>
          <b>6.3</b> {config.get('LEGAL_NAME')} не берет на себя обязательства, связанные с персональной информацией, размещенной
          Пользователям в целях, отличных от указанных в Политике обработке персональных данных.
        </p>

        <Typography sx={{ my: 2 }}>
          <b>6.4</b> Пользователь соглашается получать от {config.get('LEGAL_NAME')} рассылки рекламного и информационного характера с
          использованием тех источников связи, которые он указывал при регистрации. В любой момент можно отказаться от рассылки, для этого
          перейдите в полученном письме по соответствующей ссылки, чтобы отписаться или направьте нам свое обращение по адресу{' '}
          <Link external href={`mailto:${config.get('ADMIN_EMAIL')}`}>
            {config.get('ADMIN_EMAIL')}
          </Link>
        </Typography>

        <p>
          <b>6.5.</b> {config.get('LEGAL_NAME')} уведомило Роскомнадзор о начале обработки персональных данных и включено в Реестр
          операторов персональных данных. Обработка осуществляется в соответствии с Политикой конфиденциальности и требованиями Федерального
          закона № 152-ФЗ.
        </p>
      </section>
      <section>
        <Typography component="h4" sx={{ typography: { xs: 'h6', md: 'h4' }, m: 0, mb: 2.75 }}>
          <b>7.</b> Заключительные пункты
        </Typography>

        <p>
          <b>7.1</b> В положениях, не предусмотренных Правилами, правоотношения между сторонами регулируются нормами гражданского
          законодательства РФ.
        </p>

        <p>
          <b>7.2</b> {config.get('LEGAL_NAME')} может внести изменения в Правила, изменения будут опубликованы на странице{' '}
          {config.get('CLIENT_WEB_ORIGIN')}/termsofuse/*/agreement.pdf, «звездочка» означает дату редактирования.
        </p>

        <p>
          <b>7.3</b> У изменений нет обратного действия, они начинают действовать с момента опубликования. Если Вы не согласны с
          изменениями, Вы можете прекратить использовать Сайт и Приложение.
        </p>

        <p>
          <b>7.4</b> Если специальные нормы предусматривают иное, они преобладают над этими Правилами.
        </p>

        <p>
          <b>7.5</b> Споры по поводу заключения, выполнения условий, расторжения Соглашения, выполнения Правил рассматриваются в суде по
          месту регистрации {config.get('LEGAL_NAME')}.
        </p>

        <p>
          <b>7.6</b> По вопросам работы сервисов, исполнения правил направляйте обращения по адресу{' '}
          <Link external href={`mailto:${config.get('ADMIN_EMAIL')}`}>
            {config.get('ADMIN_EMAIL')}
          </Link>{' '}
          или через форму обратной связи.
        </p>
      </section>
    </Root>
  );
};
